import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { LaneDisruption } from '../components/LaneDisruption'
import { LANES, laneBySlug } from '../data/lanes'
import { usePageMeta } from '../lib/usePageMeta'

// Full lane object shape (fetched from /data/lanes/<slug>.json, written by prerender.lanes.mjs). The
// light index (data/lanes.ts) only carries the summary fields; this owns the rich shape.
interface CarrierRow {
  carrier_code: string
  name: string
  sailings: number
  services: number
  median_transit: number | null
  // Per-carrier arrival reliability on this lane — present only for carriers past the observation
  // gate (lane_reliability.is_displayable), null otherwise. Added by lane_pages_payload (mig 096).
  on_time_pct?: number | null
  rel_observations?: number | null
  avg_delay_hours?: number | null
  median_delay_hours?: number | null
  max_delay_hours?: number | null
}
interface Departure {
  carrier_code: string
  carrier_name: string
  service_name: string | null
  vessel_name: string | null
  etd: string | null
  eta: string | null
  transit_days: number | null
}
interface Reliability {
  on_time_pct: number
  avg_delay_hours: number
  median_delay_hours: number
  p90_delay_hours: number
  max_delay_hours?: number | null
  observations: number
  schedule_integrity_pct: number
}
interface FullLane {
  slug: string
  trade: string
  o: string
  d: string
  o_name: string
  d_name: string
  o_cc: string
  d_cc: string
  o_lat: number
  o_lng: number
  d_lat: number
  d_lng: number
  sailings: number
  carriers: number
  median_transit: number
  transship_pct: number
  carrier_rows: CarrierRow[]
  next_departures: Departure[]
  reliability: Reliability | null
}

// Visible "data as of" freshness stamp, injected at build (matches prerender.lanes.mjs).
const BUILD_DATE = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : ''

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const dt = new Date(iso)
  if (Number.isNaN(dt.getTime())) return iso
  return `${MON[dt.getUTCMonth()]} ${dt.getUTCDate()}, ${dt.getUTCFullYear()}`
}

function carrierNamesSentence(rows: CarrierRow[]): string {
  const names = rows.map((r) => r.name).filter(Boolean)
  if (!names.length) return 'multiple ocean carriers'
  if (names.length <= 3) return names.join(', ')
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`
}

// Fastest / most-frequent carrier on the lane, data-gated — mirrors prerender.lanes.mjs.
function fastestCarrierRow(rows: CarrierRow[]): CarrierRow | null {
  const withT = rows.filter((r) => r.median_transit != null)
  if (!withT.length) return null
  return withT.reduce((a, b) =>
    (b.median_transit as number) < (a.median_transit as number) ? b : a
  )
}
function mostFrequentCarrierRow(rows: CarrierRow[]): CarrierRow | null {
  if (!rows.length) return null
  return rows.reduce((a, b) => (b.sailings > a.sailings ? b : a))
}

// Carriers with a displayable on-time figure on this lane, most reliable first — mirrors
// prerender.lanes.mjs. Rows below the reliability gate carry null on_time_pct and drop out.
function ratedCarrierRows(rows: CarrierRow[]): CarrierRow[] {
  return rows
    .filter((r) => r.on_time_pct != null && r.rel_observations != null)
    .sort((a, b) => (b.on_time_pct as number) - (a.on_time_pct as number))
}
function delayPhrase(hours: number): string {
  const h = Math.round(hours)
  if (h === 0) return 'on schedule'
  const mag = Math.abs(h)
  const amount = mag >= 48 ? `${(mag / 24).toFixed(1)}d` : `${mag}h`
  return h < 0 ? `${amount} early` : `${amount} late`
}

// Direct-answer lead paragraph (~40-55 words) — the FIRST body content and paragraph-snippet target.
// Every clause data-gated so thin lanes degrade rather than invent.
function leadParagraph(lane: FullLane): string {
  const rows = lane.carrier_rows
  const top3 = rows
    .map((r) => r.name)
    .filter(Boolean)
    .slice(0, 3)
    .join(', ')
  const fastest = fastestCarrierRow(rows)
  let s1 =
    lane.median_transit != null
      ? `Ocean shipping from ${lane.o_name} to ${lane.d_name} takes about ${lane.median_transit} days in transit`
      : `Ocean shipping from ${lane.o_name} to ${lane.d_name} is run by multiple carriers`
  if (lane.carriers)
    s1 += ` (${lane.carriers} carrier${lane.carriers === 1 ? '' : 's'} sail this lane${top3 ? `, incl. ${top3}` : ''})`
  if (lane.sailings) s1 += `, based on ${lane.sailings} tracked sailings`
  s1 += '.'
  const parts = [s1]
  if (fastest?.median_transit != null)
    parts.push(`The fastest observed carrier is ${fastest.name} at ${fastest.median_transit} days.`)
  parts.push('Compare all carriers and upcoming departures below.')
  return parts.join(' ')
}

// Equirectangular projection into a 640×300 viewBox — matches prerender.lanes.mjs so hydration lines up.
function project(lat: number, lng: number, W: number, H: number) {
  return { x: ((lng + 180) / 360) * W, y: ((90 - lat) / 180) * H }
}

function RouteMap({ lane }: { lane: FullLane }) {
  const W = 640
  const H = 300
  const ok = [lane.o_lat, lane.o_lng, lane.d_lat, lane.d_lng].every(
    (v) => typeof v === 'number' && !Number.isNaN(v)
  )
  if (!ok) return null
  const o = project(lane.o_lat, lane.o_lng, W, H)
  const d = project(lane.d_lat, lane.d_lng, W, H)
  const mx = (o.x + d.x) / 2
  const my = (o.y + d.y) / 2
  const dx = d.x - o.x
  const dy = d.y - o.y
  const len = Math.hypot(dx, dy) || 1
  const lift = Math.min(90, len * 0.28)
  const cx = mx + (-dy / len) * lift
  const cy = my + (dx / len) * lift
  const gridX = Array.from({ length: 9 }, (_, i) => (i * W) / 8)
  const gridY = Array.from({ length: 5 }, (_, i) => (i * H) / 4)
  const dot = (p: { x: number; y: number }, name: string) => {
    const anchor: 'start' | 'end' = p.x > W - 120 ? 'end' : 'start'
    return (
      <g key={name}>
        <circle cx={p.x} cy={p.y} r={5.5} fill="#1f4fe6" />
        <circle
          cx={p.x}
          cy={p.y}
          r={10}
          fill="none"
          stroke="#1f4fe6"
          strokeWidth={1}
          opacity={0.4}
        />
        <text
          x={p.x + (anchor === 'end' ? -10 : 10)}
          y={p.y + 4}
          fill="var(--text-primary)"
          fontSize="13"
          fontFamily="ui-monospace, monospace"
          textAnchor={anchor}
        >
          {name}
        </text>
      </g>
    )
  }
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label={`Route map: ${lane.o_name} to ${lane.d_name}`}
      style={{
        border: '1px solid var(--border)',
        borderRadius: 12,
        background: 'var(--surface-1)',
        display: 'block',
        margin: '8px 0 4px',
      }}
    >
      <title>
        {lane.o_name} → {lane.d_name} ocean route
      </title>
      <desc>
        Great-circle style arc from {lane.o_name}, {lane.o_cc} to {lane.d_name}, {lane.d_cc}.
      </desc>
      <rect x={0} y={0} width={W} height={H} fill="var(--surface-0)" />
      {gridX.map((gx) => (
        <line
          key={`gx-${gx}`}
          x1={gx}
          y1={0}
          x2={gx}
          y2={H}
          stroke="var(--border)"
          strokeWidth={1}
          opacity={0.5}
        />
      ))}
      {gridY.map((gy) => (
        <line
          key={`gy-${gy}`}
          x1={0}
          y1={gy}
          x2={W}
          y2={gy}
          stroke="var(--border)"
          strokeWidth={1}
          opacity={0.5}
        />
      ))}
      <path
        d={`M${o.x},${o.y} Q${cx},${cy} ${d.x},${d.y}`}
        fill="none"
        stroke="#1f4fe6"
        strokeWidth={2}
        opacity={0.85}
        strokeLinecap="round"
      />
      {dot(o, lane.o_name)}
      {dot(d, lane.d_name)}
    </svg>
  )
}

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-surface-0 border border-border rounded-xl px-4 py-4">
      <div className="text-[var(--text-primary)] text-2xl font-bold font-mono leading-tight">
        {value}
      </div>
      <div className="text-[var(--text-muted)] text-xs uppercase tracking-wide mt-1.5">{label}</div>
    </div>
  )
}

function TransitBars({ rows }: { rows: CarrierRow[] }) {
  const top = rows.filter((r) => r.median_transit != null).slice(0, 8)
  if (!top.length) return null
  const maxT = Math.max(...top.map((r) => r.median_transit as number)) || 1
  const W = 640
  const rowH = 30
  const labelW = 130
  const barMax = W - labelW - 60
  const H = top.length * rowH + 8
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label="Median transit days by carrier"
      style={{ display: 'block', margin: '4px 0' }}
    >
      <title>Median transit by carrier</title>
      {top.map((r, i) => {
        const y = i * rowH + 6
        const t = r.median_transit as number
        const w = Math.max(4, (t / maxT) * barMax)
        return (
          <g key={r.carrier_code}>
            <text
              x={0}
              y={y + 15}
              fill="var(--text-primary)"
              fontSize="13"
              fontFamily="ui-monospace,monospace"
            >
              {r.name}
            </text>
            <rect x={labelW} y={y + 4} width={w} height={16} rx={4} fill="#1f4fe6" opacity={0.75} />
            <text
              x={labelW + w + 8}
              y={y + 16}
              fill="var(--text-muted)"
              fontSize="12"
              fontFamily="ui-monospace,monospace"
            >
              {t}d
            </text>
          </g>
        )
      })}
    </svg>
  )
}

const TH = 'text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]'

function LaneCta() {
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      <Link
        to="/compare"
        className="bg-signal text-on-accent font-bold px-6 py-3 rounded-lg no-underline hover:opacity-90"
      >
        Compare live schedules
      </Link>
      <a
        href="https://trackingmcp.com"
        className="border border-border px-6 py-3 rounded-lg no-underline hover:border-signal"
      >
        Track a shipment
      </a>
      <Link
        to="/mcp"
        className="border border-border px-6 py-3 rounded-lg no-underline hover:border-signal"
      >
        Use over MCP
      </Link>
    </div>
  )
}

export function LanePage() {
  const { slug: rawSlug } = useParams<{ slug: string }>()
  // Tolerate a trailing ".html" (static hosting / direct file access) or slash so a real lane never
  // falls through to the thin "not found" state.
  const slug = rawSlug?.replace(/\.html$/i, '').replace(/\/+$/, '')
  const idx = slug ? laneBySlug(slug) : undefined
  const [lane, setLane] = useState<FullLane | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'notfound'>('loading')

  // Meta from the light index so the title is right before the fetch resolves (hooks run first).
  usePageMeta(
    idx
      ? `${idx.o_name} to ${idx.d_name} Sailing Schedule & Transit Time: ${idx.sailings} sailings, ${idx.carriers} carriers | SchedulesMCP`
      : 'Lane not found · SchedulesMCP',
    idx
      ? `${idx.o_name} to ${idx.d_name} ocean schedules: ~${idx.median_transit}-day transit across ${idx.carriers} carriers, ${idx.sailings} sailings tracked. Compare lines and see upcoming departures.`
      : 'That lane page does not exist. Browse the ocean lanes SchedulesMCP covers.'
  )

  useEffect(() => {
    if (!slug || !idx) {
      setState('notfound')
      return
    }
    let alive = true
    setState('loading')
    fetch(`/data/lanes/${slug}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: FullLane) => {
        if (!alive) return
        setLane(data)
        setState('ready')
      })
      .catch(() => {
        if (alive) setState('notfound')
      })
    return () => {
      alive = false
    }
  }, [slug, idx])

  const related = useMemo(() => {
    if (!idx) return { reverse: undefined, peers: [] as typeof LANES }
    const reverseSlug = `${idx.d.toLowerCase()}-to-${idx.o.toLowerCase()}`
    const reverse = LANES.find((l) => l.slug === reverseSlug)
    const peers = LANES.filter(
      (l) => l.trade === idx.trade && l.slug !== idx.slug && l.slug !== reverseSlug
    ).slice(0, 5)
    return { reverse, peers }
  }, [idx])

  if (state === 'notfound') {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <h1
          className="font-display text-3xl mb-3"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          Lane not found
        </h1>
        <p className="text-[var(--text-secondary)] mb-6">
          We don't have a schedule page for that lane.
        </p>
        <Link to="/lanes" className="text-signal no-underline hover:underline">
          See all lanes →
        </Link>
      </main>
    )
  }

  // Substantive fallback while the full lane JSON loads — hero + headline stats straight from the light
  // index, so the page is never thin even before (or without) the fetch. Enriches to the full page below.
  if ((state === 'loading' || !lane) && idx) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <nav className="text-sm text-[var(--text-muted)] mb-5">
          <Link to="/">SchedulesMCP</Link> › <Link to="/lanes">Lanes</Link> › {idx.o_name}–
          {idx.d_name}
        </nav>
        <p
          className="m-0 mb-2 font-mono text-xs uppercase text-accent"
          style={{ letterSpacing: '0.14em' }}
        >
          {idx.trade} · lane schedule
        </p>
        <h1
          className="font-display text-4xl mb-3"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          {idx.o_name} to {idx.d_name}: ocean shipping schedules
        </h1>
        <p className="text-[var(--text-secondary)] mb-8 leading-relaxed">
          {idx.sailings} sailings from {idx.carriers} carriers · typical transit ~
          {idx.median_transit} days. Loading live departures and the carrier breakdown…
        </p>
        <div className="grid grid-cols-3 gap-4">
          {[
            ['Sailings', idx.sailings],
            ['Carriers', idx.carriers],
            ['Median transit', `${idx.median_transit}d`],
          ].map(([label, value]) => (
            <div key={label} className="bg-surface-1 rounded-xl p-4">
              <div className="text-[var(--text-muted)] text-xs uppercase tracking-wide">
                {label}
              </div>
              <div className="font-mono text-2xl mt-1">{value}</div>
            </div>
          ))}
        </div>
      </main>
    )
  }
  if (!lane) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <p className="text-[var(--text-muted)]">Loading lane schedule…</p>
      </main>
    )
  }

  const directPct = 100 - (lane.transship_pct || 0)

  return (
    <main className="max-w-3xl mx-auto px-6 pt-12 pb-16">
      <nav className="text-sm text-[var(--text-muted)] mb-5">
        <Link to="/" className="no-underline hover:underline">
          SchedulesMCP
        </Link>{' '}
        ›{' '}
        <Link to="/lanes" className="no-underline hover:underline">
          Lanes
        </Link>{' '}
        › {lane.o_name}–{lane.d_name}
      </nav>

      <p
        className="m-0 font-mono text-xs uppercase text-accent"
        style={{ letterSpacing: '0.14em' }}
      >
        {lane.trade} · lane schedule
      </p>
      <h1
        className="font-display text-4xl mt-3 mb-3"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        {lane.o_name} to {lane.d_name}: ocean shipping schedules
      </h1>
      <p className="text-[var(--text-primary)] text-lg font-medium mb-2 max-w-2xl leading-relaxed">
        {leadParagraph(lane)}
      </p>
      <p className="text-[var(--text-muted)] text-xs mb-5">
        {lane.trade} lane · schedule data as of {BUILD_DATE}.
      </p>

      <LaneDisruption pol={lane.o} pod={lane.d} polName={lane.o_name} podName={lane.d_name} />

      <RouteMap lane={lane} />

      <div
        className="grid gap-3 mt-5 mb-2"
        style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}
      >
        <StatCard value={String(lane.sailings)} label="Sailings" />
        <StatCard value={String(lane.carriers)} label="Carriers" />
        <StatCard value={`${lane.median_transit} days`} label="Median transit" />
        <StatCard
          value={`${directPct}% direct`}
          label={lane.transship_pct ? `${lane.transship_pct}% via transshipment` : 'Direct routing'}
        />
      </div>

      <LaneCta />

      <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-11 mb-3">
        How long does shipping from {lane.o_name} to {lane.d_name} take?
      </h2>
      <p className="text-[var(--text-primary)] leading-relaxed max-w-2xl mb-4">
        {lane.median_transit != null ? (
          <>
            Shipping a container from {lane.o_name} to {lane.d_name} takes about{' '}
            {lane.median_transit} days port to port on a typical sailing.
            {fastestCarrierRow(lane.carrier_rows)?.median_transit != null && (
              <>
                {' '}
                The fastest carrier we track, {fastestCarrierRow(lane.carrier_rows)?.name}, does it
                in about {fastestCarrierRow(lane.carrier_rows)?.median_transit} days.
              </>
            )}{' '}
            Transit varies by carrier and service loop. Compare each line below.
          </>
        ) : (
          <>
            Transit on the {lane.o_name}–{lane.d_name} lane varies by carrier and service loop.
            Compare each line's median below.
          </>
        )}
      </p>
      {(() => {
        const withT = lane.carrier_rows
          .filter((r) => r.median_transit != null)
          .sort((a, b) => (a.median_transit as number) - (b.median_transit as number))
        if (!withT.length) return null
        return (
          <div className="overflow-x-auto border border-border rounded-xl">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className={TH}>Carrier</th>
                  <th className={TH}>Transit time</th>
                  <th className={TH}>Sailings</th>
                </tr>
              </thead>
              <tbody>
                {withT.map((r) => (
                  <tr key={r.carrier_code} className="border-b border-border">
                    <td className="p-3 text-[var(--text-primary)] font-semibold">{r.name}</td>
                    <td className="p-3 text-[var(--text-muted)] font-mono">
                      {r.median_transit} days
                    </td>
                    <td className="p-3 text-[var(--text-muted)] font-mono">{r.sailings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })()}
      {(() => {
        const fastest = fastestCarrierRow(lane.carrier_rows)
        const frequent = mostFrequentCarrierRow(lane.carrier_rows)
        const items: string[] = []
        if (fastest?.median_transit != null)
          items.push(
            `Fastest carrier: ${fastest.name}, about ${fastest.median_transit} days port to port.`
          )
        if (frequent)
          items.push(
            `Most sailings: ${frequent.name}, ${frequent.sailings} tracked sailings${frequent.services ? ` across ${frequent.services} services` : ''}.`
          )
        if (!items.length) return null
        return (
          <ul className="mt-3 mb-2 pl-5 list-disc text-[var(--text-primary)] leading-relaxed">
            {items.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        )
      })()}

      <h2
        id="carriers"
        className="font-display font-light text-xl tracking-[-0.015em] mt-11 mb-3 scroll-mt-20"
      >
        Carriers on {lane.o_name} → {lane.d_name}
      </h2>
      <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl mb-4">
        Every line running this lane, ranked by how many sailings we track, with each carrier's
        service count and median port-to-port transit.
      </p>
      {lane.carrier_rows.length > 0 && (
        <div className="overflow-x-auto border border-border rounded-xl">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className={TH}>Carrier</th>
                <th className={TH}>Sailings</th>
                <th className={TH}>Services</th>
                <th className={TH}>Median transit</th>
              </tr>
            </thead>
            <tbody>
              {lane.carrier_rows.map((r) => (
                <tr key={r.carrier_code} className="border-b border-border">
                  <td className="p-3 text-[var(--text-primary)] font-semibold">{r.name}</td>
                  <td className="p-3 text-[var(--text-muted)] font-mono">{r.sailings}</td>
                  <td className="p-3 text-[var(--text-muted)] font-mono">{r.services}</td>
                  <td className="p-3 text-[var(--text-muted)] font-mono">
                    {r.median_transit != null ? `${r.median_transit} days` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-11 mb-3">
        Transit by carrier
      </h2>
      <TransitBars rows={lane.carrier_rows} />

      {ratedCarrierRows(lane.carrier_rows).length > 0 &&
        (() => {
          const rated = ratedCarrierRows(lane.carrier_rows)
          const best = rated[0]
          if (!best) return null
          const worst = rated.length >= 3 ? rated[rated.length - 1] : null
          return (
            <>
              <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-11 mb-3">
                Which carrier is most reliable from {lane.o_name} to {lane.d_name}?
              </h2>
              <p className="text-[var(--text-primary)] leading-relaxed max-w-2xl mb-4">
                On the {lane.o_name}–{lane.d_name} lane, {best.name} is the most reliable carrier we
                track: its ships arrive on schedule {Math.round(best.on_time_pct as number)}% of the
                time across {best.rel_observations} tracked sailings.
                {worst
                  ? ` The least reliable is ${worst.name} at ${Math.round(worst.on_time_pct as number)}%.`
                  : ''}{' '}
                “On schedule” means the box arrived within a day of the carrier’s own earlier
                forecast, measured from our tracking, not the published timetable.
              </p>
              <div className="overflow-x-auto border border-border rounded-xl">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className={TH}>Carrier</th>
                      <th className={TH}>On-time arrivals</th>
                      <th className={TH}>Typical delay</th>
                      <th className={TH}>Worst case</th>
                      <th className={TH}>Tracked sailings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rated.map((r) => (
                      <tr key={r.carrier_code} className="border-b border-border">
                        <td className="p-3 text-[var(--text-primary)] font-semibold">{r.name}</td>
                        <td className="p-3 text-[var(--text-primary)] font-mono font-semibold">
                          {Math.round(r.on_time_pct as number)}%
                        </td>
                        <td className="p-3 text-[var(--text-muted)] font-mono">
                          {r.median_delay_hours != null ? delayPhrase(r.median_delay_hours) : '—'}
                        </td>
                        <td className="p-3 text-[var(--text-muted)] font-mono">
                          {r.max_delay_hours != null ? delayPhrase(r.max_delay_hours) : '—'}
                        </td>
                        <td className="p-3 text-[var(--text-muted)] font-mono">
                          {r.rel_observations}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        })()}

      <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-11 mb-3">
        Upcoming departures
      </h2>
      <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl mb-4">
        The next confirmed sailings on this lane: vessel, carrier, service loop, and dates.
      </p>
      {lane.next_departures.length > 0 ? (
        <div className="overflow-x-auto border border-border rounded-xl">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className={TH}>Vessel</th>
                <th className={TH}>Carrier</th>
                <th className={TH}>Service</th>
                <th className={TH}>Departs</th>
                <th className={TH}>Arrives</th>
                <th className={TH}>Transit</th>
              </tr>
            </thead>
            <tbody>
              {lane.next_departures.slice(0, 10).map((dp, i) => (
                <tr key={`${dp.vessel_name}-${dp.etd}-${i}`} className="border-b border-border">
                  <td className="p-3 text-[var(--text-primary)] font-semibold">
                    {/* TBN arrives from the API where the line wrote one; null is no ship given. */}
                    {dp.vessel_name || '—'}
                  </td>
                  <td className="p-3 text-[var(--text-muted)]">
                    {dp.carrier_name || dp.carrier_code || '—'}
                  </td>
                  <td className="p-3 text-[var(--text-muted)]">{dp.service_name || '—'}</td>
                  <td className="p-3 text-[var(--text-muted)] font-mono">{fmtDate(dp.etd)}</td>
                  <td className="p-3 text-[var(--text-muted)] font-mono">{fmtDate(dp.eta)}</td>
                  <td className="p-3 text-[var(--text-muted)] font-mono">
                    {dp.transit_days != null ? `${dp.transit_days}d` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[var(--text-secondary)] leading-relaxed">
          Schedule refresh pending: the next confirmed departures on this lane are being re-pulled
          from the carriers. Compare the live tool for the current board.
        </p>
      )}

      {lane.reliability && (
        <>
          <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-11 mb-3">
            Observed reliability on this lane
          </h2>
          <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl mb-4">
            Measured from actual vs scheduled arrivals across tracked port calls on this lane, not
            the schedule the carriers published.
          </p>
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}
          >
            <StatCard value={`${lane.reliability.on_time_pct}%`} label="On time" />
            <StatCard value={`${lane.reliability.avg_delay_hours}h`} label="Avg delay" />
            {lane.reliability.max_delay_hours != null && lane.reliability.max_delay_hours > 0 ? (
              <StatCard value={`${lane.reliability.max_delay_hours}h`} label="Worst case" />
            ) : (
              <StatCard value={`${lane.reliability.p90_delay_hours}h`} label="p90 delay" />
            )}
            <StatCard value={String(lane.reliability.observations)} label="Observations" />
          </div>
        </>
      )}

      <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-11 mb-1">
        {lane.o_name} to {lane.d_name}: FAQ
      </h2>
      <div>
        <div className="border-b border-border py-4">
          <h3 className="text-[var(--text-primary)] text-base mb-2">
            How long does shipping from {lane.o_name} to {lane.d_name} take?
          </h3>
          <p className="text-[var(--text-secondary)] leading-relaxed m-0">
            Typical ocean transit on the {lane.o_name}–{lane.d_name} lane is around{' '}
            {lane.median_transit} days port to port, though it varies by carrier and service loop.
            See the carrier table above for each line's median.
          </p>
        </div>
        <div className="border-b border-border py-4">
          <h3 className="text-[var(--text-primary)] text-base mb-2">
            Which carriers sail {lane.o_name} to {lane.d_name}?
          </h3>
          <p className="text-[var(--text-secondary)] leading-relaxed m-0">
            {lane.carriers} carriers run this lane, including{' '}
            {carrierNamesSentence(lane.carrier_rows)}.
          </p>
        </div>
        <div className="border-b border-border py-4">
          <h3 className="text-[var(--text-primary)] text-base mb-2">
            How often do ships depart {lane.o_name} to {lane.d_name}?
          </h3>
          <p className="text-[var(--text-secondary)] leading-relaxed m-0">
            Across the schedule window we track about {lane.sailings} sailings on this lane from{' '}
            {lane.carriers} carriers, so departures run multiple times per week.
          </p>
        </div>
        {ratedCarrierRows(lane.carrier_rows).length > 0 &&
          (() => {
            const best = ratedCarrierRows(lane.carrier_rows)[0]
            if (!best) return null
            return (
              <div className="border-b border-border py-4">
                <h3 className="text-[var(--text-primary)] text-base mb-2">
                  Which carrier is most reliable from {lane.o_name} to {lane.d_name}?
                </h3>
                <p className="text-[var(--text-secondary)] leading-relaxed m-0">
                  {best.name} is the most reliable line we track on this lane. Its ships arrive on
                  schedule {Math.round(best.on_time_pct as number)}% of the time across{' '}
                  {best.rel_observations} tracked sailings, measured from actual vs forecast
                  arrivals.
                </p>
              </div>
            )
          })()}
      </div>

      <LaneCta />

      {(related.reverse || related.peers.length > 0) && (
        <>
          <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-12 mb-3">
            Related lanes
          </h2>
          <div className="flex flex-wrap gap-2.5">
            {related.reverse && (
              <Link
                to={`/lanes/${related.reverse.slug}`}
                className="bg-surface-0 border rounded-full px-3.5 py-1.5 text-sm text-signal no-underline hover:underline"
                style={{ borderColor: 'rgb(var(--accent-rgb) / 0.3)' }}
              >
                {related.reverse.o_name} → {related.reverse.d_name} (return)
              </Link>
            )}
            {related.peers.map((p) => (
              <Link
                key={p.slug}
                to={`/lanes/${p.slug}`}
                className="bg-surface-0 border border-border rounded-full px-3.5 py-1.5 text-sm text-[var(--text-secondary)] no-underline hover:border-signal"
              >
                {p.o_name} → {p.d_name}
              </Link>
            ))}
            <Link
              to="/lanes"
              className="rounded-full px-3.5 py-1.5 text-sm text-signal no-underline hover:underline border"
              style={{ borderColor: 'rgb(var(--accent-rgb) / 0.3)' }}
            >
              All lanes →
            </Link>
          </div>
        </>
      )}
    </main>
  )
}

export default LanePage
